import { type FC, useRef, useEffect, useState, useMemo } from "react";
import { cn } from "@/components/podkit/lib/cn";
import { IconChevronRight } from "@/assets/icons/geist/IconChevronRight";
import { IconCircleCheckedFilled } from "@/assets/icons/central/IconCircleCheckedFilled";
import { IconCircleDashed } from "central-icons/IconCircleDashed";
import { IconCircleDotted } from "central-icons/IconCircleDotted";
import { IconCircle } from "@/assets/icons/geist/IconCircle";
import { OnaThinking } from "@/components/ona/OnaThinking";
import { TextShimmer } from "@/components/flexkit/TextShimmer";
import { AgentResponseBlock_TodoItem_Phase } from "gitpod-next-api/gitpod/v1/agent_pb";
import type { AgentMessageFragment, AgentMessageFragmentType } from "@/hooks/use-stream-conversation";
import type { AgentTodoGroupItem } from "@/utils/message-grouping";
import { Fragment } from "@/components/ai/Fragment";
import type { PlainAgentExecution } from "@/queries/agent-queries";
import { Collapsible, CollapsibleContent } from "@/components/flexkit/Collapsible";
import { AgentExecutionState, agentExecutionState } from "@/utils/agent-executions";
import { OnaWaiting } from "@/components/ona/OnaWaiting";

export type TodoContainerProps = {
    todo: AgentTodoGroupItem;
    isLastGroup: boolean;
    agentExecution: PlainAgentExecution;
    onClick?: (block: AgentMessageFragment) => void;
};

type OpenState = {
    isOpen: boolean;
    changedByUser: boolean;
};

export const TodoContainer: FC<TodoContainerProps> = ({ todo, isLastGroup, agentExecution, onClick }) => {
    const { todoId, todoTitle, todoStatus } = todo;

    const fragments = useMemo(() => todo.fragments.filter(({ type }) => !IgnoreFragments?.has(type)), [todo.fragments]);
    const fragmentCount = fragments.length;
    const hasContent = fragmentCount > 0;

    const [openState, setOpenState] = useState<OpenState>({ isOpen: false, changedByUser: false });
    const scrollContainerRef = useRef<HTMLDivElement>(null);

    const agentState = agentExecutionState(agentExecution);
    const isAgentWorking = agentState === AgentExecutionState.WORKING;

    const isPending = todoStatus === AgentResponseBlock_TodoItem_Phase.PENDING;
    const isInProgress = todoStatus === AgentResponseBlock_TodoItem_Phase.IN_PROGRESS;
    const isCompleted = todoStatus === AgentResponseBlock_TodoItem_Phase.DONE;
    const isCleared = todoStatus === AgentResponseBlock_TodoItem_Phase.CLEARED;
    const isPostponed = todoStatus === AgentResponseBlock_TodoItem_Phase.PENDING && !isLastGroup;
    const onaIsWorkingOnCurrentTodo = isInProgress && isAgentWorking && isLastGroup;
    const onaIsWaitingForUser = isInProgress && !isAgentWorking && isLastGroup;

    useEffect(() => {
        // If the user changes the open state we never want the system to modify it.
        if (openState.changedByUser) {
            return;
        }

        if (onaIsWaitingForUser && hasContent) {
            setOpenState({ isOpen: true, changedByUser: false });
            if (scrollContainerRef.current) {
                // Auto-scroll to bottom when in progress and is last
                scrollContainerRef.current.scrollTop = scrollContainerRef.current.scrollHeight;
            }
        } else {
            setOpenState({ isOpen: false, changedByUser: false });
        }
    }, [hasContent, onaIsWaitingForUser, openState.changedByUser]);

    const getStatusIcon = () => {
        if (isPostponed) {
            return <IconCircleDotted className="size-5 flex-shrink-0 text-content-muted" />;
        } else if (isPending || isCleared) {
            return <IconCircle size="sm" className="size-5 flex-shrink-0 text-content-muted" />;
        } else if (isCompleted) {
            return <IconCircleCheckedFilled className={cn("size-5 text-content-success")} />;
        } else if (onaIsWorkingOnCurrentTodo) {
            return <OnaThinking state="thinking" size="sm" className="size-5" />;
        } else if (onaIsWaitingForUser) {
            return <OnaWaiting cycleInterval={1000} size="sm" className="size-[17px]" />;
        } else if (isInProgress) {
            return <IconCircleDashed className="size-5 flex-shrink-0 text-content-muted" />;
        }
        return null;
    };

    const getStatusLabel = () => {
        if (isCompleted) return "Completed";
        if (isInProgress) return "In progress";
        if (isCleared) return "Cleared";
        if (isPostponed) return "Postponed";
        return "Pending";
    };

    return (
        <div
            id={`todo-${todoId}`}
            className={cn("border-b border-border-base transition-colors last:border-b-0", {
                "border-transparent": isPending,
            })}
        >
            {/* Todo Header */}
            <button
                onClick={() => {
                    if (hasContent) setOpenState({ isOpen: !openState.isOpen, changedByUser: true });
                }}
                className={cn(
                    "flex w-full items-center justify-between gap-4 px-4 py-3 text-left transition-all duration-300 ease-out",
                    hasContent && "hover:bg-surface-muted",
                    !hasContent && "cursor-default",
                )}
                aria-expanded={openState.isOpen}
                aria-label={`${getStatusLabel()}: ${todoTitle}`}
                data-tracking-id="todo-container-toggle"
                disabled={!hasContent || (fragmentCount !== undefined && fragmentCount === 0)}
            >
                {/* Left side: Status icon + Text content (title + fragment preview) */}
                <div
                    className={cn("flex min-w-0 flex-1 items-center gap-2", {
                        "opacity-50": isPending || isCleared,
                    })}
                >
                    <div className="flex h-5 w-5 flex-shrink-0 items-center justify-center">{getStatusIcon()}</div>
                    <div className="flex min-w-0 flex-1 flex-col gap-0">
                        {/* Title */}
                        {isInProgress && isAgentWorking && isLastGroup ? (
                            <TextShimmer className="min-w-0 truncate text-base text-content-primary">
                                {todoTitle}
                            </TextShimmer>
                        ) : (
                            <h2
                                className={cn(
                                    "flex min-w-0 items-center justify-between truncate text-base text-content-primary transition-colors duration-300",
                                    {
                                        "text-content-muted":
                                            isCompleted || isPending || isCleared || (isInProgress && !isLastGroup),
                                        "line-through": isCleared,
                                    },
                                )}
                            >
                                {todoTitle}
                                {isPostponed && <span className="text-right text-xs">Postponed</span>}
                            </h2>
                        )}

                        {/* Progress */}
                        <Collapsible open={isInProgress && isLastGroup}>
                            <CollapsibleContent>
                                <TextShimmer className="min-w-0 truncate font-mono text-sm" shimmer={isAgentWorking}>
                                    {currentFragmentText(todo)}
                                </TextShimmer>
                            </CollapsibleContent>
                        </Collapsible>
                    </div>
                </div>

                {/* Right side: Controls (chevron only) - vertically centered */}
                {hasContent && (
                    <div className="flex flex-shrink-0 items-center">
                        <IconChevronRight
                            size="sm"
                            aria-hidden={isPending}
                            className={cn(
                                "flex-shrink-0 opacity-100 transition-[opacity,transform] duration-200",
                                {
                                    "opacity-40": isCleared,
                                },
                                openState.isOpen && "rotate-90",
                            )}
                        />
                    </div>
                )}
            </button>

            {/* Todo Content - Simple version without complex animations */}
            <Collapsible open={openState.isOpen}>
                <CollapsibleContent>
                    <div
                        ref={scrollContainerRef}
                        className={cn(
                            "w-full overflow-y-auto border-t border-border-base px-4 py-2 text-base",
                            "max-h-[40vh]",
                        )}
                    >
                        <div className="flex flex-col gap-4">
                            {fragments.map((fragment, fragIndex) => {
                                return (
                                    <Fragment
                                        key={`${todo.todoId}-${fragIndex}`}
                                        fragment={fragment}
                                        agentExecution={agentExecution}
                                        shouldHighlightFragment={false}
                                        onClick={onClick}
                                        data-tracking-id="default-tracking-id"
                                        ignore={IgnoreFragments}
                                    />
                                );
                            })}
                        </div>
                    </div>
                </CollapsibleContent>
            </Collapsible>
        </div>
    );
};

// Todo items are handled at the grouping level, so we don't
// want to render them as fragments
const IgnoreFragments = new Set<AgentMessageFragmentType>(["todoItem", "todoGroup", "userInputSeen"]);

function currentFragmentText(todo: AgentTodoGroupItem) {
    const currentFragment = todo.fragments.length > 0 ? todo.fragments[todo.fragments.length - 1] : null;
    let text = "Thinking...";
    if (currentFragment && currentFragment.type === "text" && currentFragment.content) {
        text = currentFragment.content.split("\n")[0].substring(0, 100);
    } else if (currentFragment && currentFragment.type === "action" && currentFragment.intentDescription) {
        text = currentFragment.intentDescription;
    }
    return text;
}
